import { useState, useEffect } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';

import WorkQueueHeader from '../components/WorkQueue/WorkQueueHeader';
import WorkQueueKPIs from '../components/WorkQueue/WorkQueueKPIs';
import WorkQueueFilters from '../components/WorkQueue/WorkQueueFilters';
import WorkQueueTable from '../components/WorkQueue/WorkQueueTable';
import SalesforceTaskDrawer from '../components/Tasks/SalesforceTaskDrawer';

import { useIRWorkstation } from '../contexts/IRWorkstationContext';
import { useNotificationContext } from 'contexts/NotificationContext';
import useSalesforceWorkQueue from 'hooks/useSalesforceWorkQueue';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

const DEFAULT_SORT_MODEL = [{ field: 'priority', sort: 'asc' }];

const WorkQueue = () => {
	const [initialPageLoaded, setInitialPageLoaded] = useState(false);
	const [selectedTask, setSelectedTask] = useState(null);
	const [showCompleted, setShowCompleted] = useState(false);
	const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);

	const { tenant } = useIRWorkstation();
	const { showNotification } = useNotificationContext();

	const { trackButtonClick, trackPageLoad } = useTrackEvent({
		tenant,
		product: 'IEM',
	});

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.WorkQueue);
	}, [trackPageLoad]);

	// Toggle OFF shows open tasks; toggle ON includes open and completed tasks
	const taskFilter = showCompleted ? 'all' : 'open';

	const {
		tasks: salesforceTasks,
		totalCount,
		loading,
		loadingMore,
		loadMoreError,
		refreshing,
		refreshError,
		error,
		hasMore,
		loadMore,
		refetch: refreshTasks,
	} = useSalesforceWorkQueue(taskFilter, sortModel);

	// Keep the initial loading skeleton separate from later refreshes
	useEffect(() => {
		if (!loading && !initialPageLoaded) {
			setInitialPageLoaded(true);
		}
	}, [loading, initialPageLoaded]);

	// Show pagination errors without replacing previously loaded rows
	useEffect(() => {
		if (loadMoreError) {
			showNotification(loadMoreError, 'error');
		}
	}, [loadMoreError, showNotification]);

	// Show refresh errors while preserving previously loaded rows
	useEffect(() => {
		if (refreshError) {
			showNotification(refreshError, 'error');
		}
	}, [refreshError, showNotification]);

	const pageLoading = loading && !initialPageLoaded;

	const handleTaskUpdated = async (updatedTask) => {
		setSelectedTask(updatedTask);
		await refreshTasks();
	};

	const handleShowCompletedChange = (checked) => {
		setShowCompleted(checked);
		setSortModel(DEFAULT_SORT_MODEL);

		trackButtonClick(
			checked
				? ButtonClickTargets.WorkQueueAll
				: ButtonClickTargets.WorkQueueOpen
		);
	};

	const recordsLoaded = salesforceTasks.length;
	const allRecordsLoaded = totalCount === null || recordsLoaded >= totalCount;

	const loadedRecordsCount = allRecordsLoaded
		? `${recordsLoaded} records loaded`
		: `${recordsLoaded} of ${totalCount} records loaded`;

	// Restore the default sort when the user clears the grid sorting
	const handleSortModelChange = (newSortModel) => {
		setSortModel(newSortModel.length > 0 ? newSortModel : DEFAULT_SORT_MODEL);
	};

	return (
		<Box
			sx={{
				flex: 1,
				minWidth: 0,
				overflowY: 'auto',
				backgroundColor: 'background.default',
				padding: '12px 20px',
				display: 'flex',
				flexDirection: 'column',
				gap: '14px',
			}}
		>
			<WorkQueueHeader />

			<WorkQueueKPIs loading={pageLoading} />

			<Box
				sx={{
					backgroundColor: 'background.paper',
					border: '0.5px solid',
					borderColor: 'border.light',
					borderRadius: '8px',
					overflow: 'hidden',
					minHeight: 0,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						px: '13px',
						py: '10px',
						borderBottom: '0.5px solid',
						borderColor: 'border.light',
					}}
				>
					<Typography
						sx={{
							fontSize: '12px',
							fontWeight: 600,
						}}
					>
						Work Queue Items
					</Typography>

					{/* Avoid displaying stale counts while refreshing the queue */}
					{loading || refreshing ? (
						<Skeleton variant='text' width={120} height={20} />
					) : (
						<Typography
							sx={{
								fontSize: '11px',
								color: 'text.secondary',
							}}
						>
							{loadedRecordsCount}
						</Typography>
					)}
				</Box>

				<WorkQueueFilters
					loading={pageLoading}
					showCompleted={showCompleted}
					onShowCompletedChange={handleShowCompletedChange}
				/>

				{error ? (
					<Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
						<Typography
							sx={{
								fontSize: '12px',
								color: 'text.secondary',
							}}
						>
							Unable to load Work Queue items. Check your CRM connection.
						</Typography>
					</Box>
				) : (
					<WorkQueueTable
						key={taskFilter}
						tasks={salesforceTasks}
						loading={loading}
						refreshing={refreshing}
						loadingMore={loadingMore}
						hasMore={hasMore && !loadMoreError}
						onLoadMore={loadMore}
						onOpenTaskDrawer={setSelectedTask}
						sortModel={sortModel}
						onSortModelChange={handleSortModelChange}
					/>
				)}
			</Box>

			{selectedTask && (
				<SalesforceTaskDrawer
					open={Boolean(selectedTask)}
					task={selectedTask}
					tenant={tenant}
					onClose={() => setSelectedTask(null)}
					onTaskUpdated={handleTaskUpdated}
					salesforceOrgUrl={tenant?.salesforce_org_url}
				/>
			)}
		</Box>
	);
};

export default WorkQueue;
